import { Shell } from '@/components/Shell';
import { EmptyRow } from '@/components/ui/EmptyState';

import { Pill, stateLabel, stateToTone } from '@/components/ui/Pill';
import { type SortState, SortTh, sortBy, toggleSort } from '@/components/ui/SortTh';
import { adminApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/bookings/')({
  beforeLoad: async ({ location }) => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login', search: { next: location.pathname } as Record<string, string> });
    }
  },
  component: BookingsPage,
});

const STATES = [
  { v: '', l: 'TODOS' },
  { v: 'reserved_pending_payment', l: 'AGUARDA PGT.' },
  { v: 'confirmed', l: 'CONFIRMADAS' },
  { v: 'in_progress', l: 'EM CURSO' },
  { v: 'completed', l: 'CONCLUÍDAS' },
  { v: 'cancelled', l: 'CANCELADAS' },
];

function BookingsPage() {
  const [q, setQ] = useState('');
  const [state, setState] = useState('');
  const [sort, setSort] = useState<SortState<'dates'>>({ key: 'dates', dir: 'desc' });
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'bookings', q, state],
    queryFn: () => adminApi.bookings({ q: q || undefined, state: state || undefined }),
  });

  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short' });
  const count = data?.paging.count ?? 0;

  return (
    <Shell page="RESERVAS" sub={`GESTÃO · ${count} TOTAL`}>
      {/* Filter row */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        {/* State filter chips */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STATES.map((s) => (
            <button
              key={s.v || 'all'}
              type="button"
              onClick={() => setState(s.v)}
              className={`chip${state === s.v ? ' active' : ''}`}
            >
              {s.l}
            </button>
          ))}
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <input
            type="search"
            placeholder="Código, matrícula, cliente…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--lv-line)',
              background: '#fff',
              fontSize: 12,
              width: 240,
              borderRadius: 0,
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid var(--lv-line)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--lv-night)' }}>
              <SortTh width="120px">#</SortTh>
              <SortTh>CLIENTE</SortTh>
              <SortTh width="160px">VIATURA</SortTh>
              <SortTh
                width="160px"
                sortKey="dates"
                sort={sort}
                onSort={(k) => setSort((s) => toggleSort(s, k))}
              >
                DATAS
              </SortTh>
              <SortTh width="140px">ESTADO</SortTh>
              <SortTh width="60px"> </SortTh>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  A carregar…
                </td>
              </tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <EmptyRow colSpan={6} message="Sem reservas para este filtro." />
            )}
            {sortBy(data?.items ?? [], (b) => b.fromAt, sort.dir).map((b) => {
              const initials = (b.customer.name ?? b.customer.email)
                .split(' ')
                .map((p) => p[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();

              return (
                <tr
                  key={b.id}
                  className="row-link"
                  style={{ borderTop: '1px solid var(--lv-line)' }}
                  onClick={() => navigate({ to: '/bookings/$id', params: { id: b.id } })}
                >
                  {/* Code */}
                  <td style={{ padding: '14px 16px' }}>
                    <Link
                      to="/bookings/$id"
                      params={{ id: b.id }}
                      style={{
                        fontFamily: 'var(--lv-font-display)',
                        fontSize: 16,
                        fontWeight: 900,
                        color: 'var(--lv-brand)',
                        textDecoration: 'none',
                        letterSpacing: '0.01em',
                        textTransform: 'uppercase',
                      }}
                    >
                      #{b.code}
                    </Link>
                  </td>

                  {/* Customer */}
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          background: 'var(--lv-brand-deep)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 10,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {initials}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                          {b.customer.name ?? b.customer.email}
                        </div>
                        {b.customer.name && (
                          <div style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>{b.customer.email}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Vehicle */}
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{b.vehicle.name}</div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--lv-gray-500)',
                        fontFamily: 'var(--lv-font-mono)',
                        marginTop: 1,
                      }}
                    >
                      {b.vehicle.plate}
                    </div>
                  </td>

                  {/* Dates */}
                  <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--lv-gray-500)' }}>
                    {dateFmt.format(new Date(b.fromAt))}
                    <span style={{ margin: '0 4px', opacity: 0.5 }}>→</span>
                    {dateFmt.format(new Date(b.toAt))}
                  </td>

                  {/* State */}
                  <td style={{ padding: '14px 16px' }}>
                    <Pill label={stateLabel(b.state)} tone={stateToTone(b.state)} />
                  </td>

                  {/* Actions */}
                  <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                    <Link
                      to="/bookings/$id"
                      params={{ id: b.id }}
                      style={{
                        fontSize: 18,
                        color: 'var(--lv-gray-500)',
                        textDecoration: 'none',
                        lineHeight: 1,
                      }}
                    >
                      ⋯
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Footer */}
        {data && (
          <div
            style={{
              borderTop: '1px solid var(--lv-line)',
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 12,
              color: 'var(--lv-gray-500)',
            }}
          >
            <span>
              Mostrando {Math.min(data.items.length, data.paging.limit)} de {count}
            </span>
          </div>
        )}
      </div>
    </Shell>
  );
}
